import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

let sdkPromise = null;

function loadPayPalSdk(clientId) {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    if (window.paypal) return resolve(window.paypal);
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = () => { sdkPromise = null; reject(new Error("Failed to load PayPal SDK")); };
    document.body.appendChild(s);
  });
  return sdkPromise;
}

export default function PayPalCheckout({ beat, licenseType, customerEmail, customerName, onSuccess, disabled }) {
  const ref = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    let buttons;
    async function init() {
      try {
        setLoading(true);
        const { data: cfg } = await api.get("/config");
        if (!cfg.configured || !cfg.paypal_client_id) {
          setErr("PayPal is not configured yet. The producer needs to set up PayPal credentials.");
          setLoading(false);
          return;
        }
        const paypal = await loadPayPalSdk(cfg.paypal_client_id);
        if (!mounted || !ref.current) return;
        ref.current.innerHTML = "";
        buttons = paypal.Buttons({
          style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal" },
          onClick: async (data, actions) => {
            if (!customerEmail || !/^\S+@\S+\.\S+$/.test(customerEmail)) {
              toast.error("Enter a valid email to receive your download link.");
              return actions.reject();
            }
            if (disabled) return actions.reject();
            return actions.resolve();
          },
          createOrder: async () => {
            const { data } = await api.post("/orders", {
              beat_id: beat.id,
              license_type: licenseType,
              customer_email: customerEmail,
              customer_name: customerName || "",
            });
            return data.id;
          },
          onApprove: async (data) => {
            try {
              const { data: cap } = await api.post(`/orders/${data.orderID}/capture`);
              toast.success("Payment captured! Your download is ready.");
              onSuccess?.({ orderId: data.orderID, ...cap });
            } catch (e) {
              toast.error(e.response?.data?.detail || "Capture failed");
            }
          },
          onError: (e) => {
            console.error(e);
            toast.error("PayPal error. Try again.");
          },
        });
        await buttons.render(ref.current);
        setLoading(false);
      } catch (e) {
        setErr(e.message || "PayPal failed to load");
        setLoading(false);
      }
    }
    init();
    return () => {
      mounted = false;
      try { buttons?.close?.(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat?.id, licenseType, customerEmail, customerName, disabled]);

  return (
    <div className="mt-4" data-testid="paypal-checkout-container">
      {loading && !err && <div className="text-sm text-[var(--text-dim)]">Loading PayPal…</div>}
      {err && (
        <div className="text-sm text-[var(--text-dim)] card-flat p-3">
          {err}
        </div>
      )}
      <div ref={ref} />
    </div>
  );
}
